/**
 * Custom slider (Hofman Animal Care)
 *
 * Self-contained, dependency-free custom element die drag-to-scroll (muis)
 * toevoegt aan een horizontale scroll-container. Touch/swipe werkt al native
 * via overflow-x: auto, dus dat hoeft hier niet afgehandeld te worden.
 */
class CustomSliderCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {boolean} */
  #pointerDown = false;
  /** @type {boolean} */
  #dragging = false;
  /** @type {number} */
  #startX = 0;
  /** @type {number} */
  #startScrollLeft = 0;
  /** @type {number} */
  #lastMoveX = 0;
  /** @type {number} */
  #lastVelocity = 0;
  /** @type {AbortController | null} */
  #abort = null;

  static DRAG_THRESHOLD = 5;
  // Hoeveel de laatste sleepsnelheid de eindpositie "doorgooit" (momentum).
  static MOMENTUM_FACTOR = 12;

  connectedCallback() {
    this.#track = this.querySelector('.custom-slider__track');
    if (!this.#track) return;

    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    this.setAttribute('data-draggable', '');

    this.#track.addEventListener('pointerdown', this.#onPointerDown, opts);
    this.#track.addEventListener('pointermove', this.#onPointerMove, opts);
    this.#track.addEventListener('pointerup', this.#onPointerUp, opts);
    this.#track.addEventListener('pointercancel', this.#onPointerUp, opts);
    this.#track.addEventListener('pointerleave', this.#onPointerUp, opts);
    this.#track.addEventListener('dragstart', this.#preventDefault, opts);
    this.#track.addEventListener('click', this.#onClickCapture, { ...opts, capture: true });

    // Toetsenbord-scrollbaarheid: de carousel is focusbaar (tabindex) en met de
    // pijltjestoetsen (+ Home/End) scrollt de gebruiker door de slides.
    this.addEventListener('keydown', this.#onKeydown, opts);

    window.addEventListener('resize', this.#onResize, opts);
    this.#applyBleed();
    // Herbereken zodra layout/afbeeldingen/fonts gesettled zijn.
    requestAnimationFrame(() => this.#applyBleed());
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    if (this.#resizeRaf) cancelAnimationFrame(this.#resizeRaf);
  }

  /**
   * Laat de carousel aan beide kanten doorlopen tot de randen van het scherm,
   * zodat slides die wegscrollen links en rechts zichtbaar blijven. De eerste
   * slide begint wel binnen de container (via padding op de track), en de
   * slidegrootte blijft gebaseerd op de containerbreedte. We meten de
   * werkelijke positie/breedte, zodat dit werkt ongeacht hoe de sectie genest
   * of beperkt is in breedte.
   */
  #applyBleed = () => {
    // Reset, zodat we de natuurlijke positie/breedte binnen de container meten.
    this.style.marginInlineStart = '0px';
    this.style.marginInlineEnd = '0px';

    const rect = this.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const leftInset = Math.max(0, Math.round(rect.left));
    const rightGap = Math.max(0, Math.round(viewportWidth - rect.right));
    const baseWidth = Math.round(rect.width);

    // Slidegrootte op de containerbreedte baseren, niet op de volle viewport.
    this.style.setProperty('--cs-base-width', `${baseWidth}px`);

    // Beide kanten laten doorlopen tot de schermrand.
    this.style.marginInlineStart = leftInset ? `-${leftInset}px` : '0px';
    this.style.marginInlineEnd = rightGap ? `-${rightGap}px` : '0px';

    // Eerste slide begint binnen de container; weggescrollde slides blijven
    // links zichtbaar in het "bleed"-gebied. Aan het eind voegen we evenveel
    // ruimte toe, zodat de laatste slide doorscrollt tot binnen de container
    // (en niet tegen de schermrand plakt).
    if (this.#track) {
      this.#track.style.paddingInlineStart = leftInset ? `${leftInset}px` : '';
      this.#track.style.scrollPaddingInlineStart = leftInset ? `${leftInset}px` : '';
      this.#track.style.paddingInlineEnd = rightGap ? `${rightGap}px` : '';
      this.#track.style.scrollPaddingInlineEnd = rightGap ? `${rightGap}px` : '';
    }
  };

  /** @type {number} */
  #resizeRaf = 0;

  #onResize = () => {
    if (this.#resizeRaf) cancelAnimationFrame(this.#resizeRaf);
    this.#resizeRaf = requestAnimationFrame(() => this.#applyBleed());
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0 || !this.#track) return;
    this.#pointerDown = true;
    this.#dragging = false;
    this.#startX = event.clientX;
    this.#lastMoveX = event.clientX;
    this.#lastVelocity = 0;
    this.#startScrollLeft = this.#track.scrollLeft;
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#pointerDown || !this.#track) return;

    const delta = event.clientX - this.#startX;

    if (!this.#dragging) {
      if (Math.abs(delta) < CustomSliderCarousel.DRAG_THRESHOLD) return;
      this.#dragging = true;
      this.setAttribute('data-dragging', '');
      this.setAttribute('data-snap-off', '');
      this.#track.setPointerCapture?.(event.pointerId);
    }

    event.preventDefault();
    this.#track.scrollLeft = this.#startScrollLeft - delta;

    // Bewaar de snelheid van de laatste beweging voor het momentum bij loslaten.
    this.#lastVelocity = event.clientX - this.#lastMoveX;
    this.#lastMoveX = event.clientX;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#pointerDown) return;
    this.#pointerDown = false;
    this.#track?.releasePointerCapture?.(event.pointerId);
    // Cursor terug naar "grab", maar snap blijft nog even uit tijdens de
    // smooth-scroll naar het dichtstbijzijnde snappunt.
    this.removeAttribute('data-dragging');

    if (this.#dragging) {
      this.#snapToNearest();
    } else {
      this.removeAttribute('data-snap-off');
    }
  };

  /**
   * Scrollt na het loslaten van een drag smooth naar het dichtstbijzijnde
   * snappunt, met een lichte momentum-projectie op basis van de laatste
   * sleepsnelheid. Snap wordt pas weer ingeschakeld zodra de scroll is
   * uitgevoerd, zodat de browser niet instant naar zijn plek springt.
   */
  #snapToNearest = () => {
    const track = this.#track;
    if (!track) return;

    const slides = track.querySelectorAll('.custom-slider__slide');
    if (!slides.length) {
      this.removeAttribute('data-snap-off');
      return;
    }

    const padStart = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
    const maxScroll = track.scrollWidth - track.clientWidth;

    // Pointer naar rechts (positieve velocity) onthult vorige slides, dus de
    // scrollpositie moet dan kleiner worden; vandaar het minteken.
    const projected =
      track.scrollLeft - this.#lastVelocity * CustomSliderCarousel.MOMENTUM_FACTOR;

    let target = 0;
    let smallestDistance = Infinity;
    for (const slide of slides) {
      const slidePosition = slide.offsetLeft - padStart;
      const distance = Math.abs(slidePosition - projected);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        target = slidePosition;
      }
    }

    target = Math.max(0, Math.min(target, maxScroll));

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#reenableSnapAfterScroll();
    track.scrollTo({ left: target, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  /**
   * Schakelt scroll-snap weer in zodra de smooth-scroll is afgerond (via het
   * `scrollend`-event), met een timeout als fallback voor browsers zonder
   * `scrollend`-ondersteuning.
   */
  #reenableSnapAfterScroll = () => {
    const track = this.#track;
    if (!track) return;

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      track.removeEventListener('scrollend', finish);
      this.removeAttribute('data-snap-off');
    };

    const timer = setTimeout(finish, 700);
    track.addEventListener('scrollend', finish, { once: true });
  };

  /** @param {MouseEvent} event */
  #onClickCapture = (event) => {
    // Voorkom dat een drag-actie per ongeluk een link/knop activeert.
    if (this.#dragging) {
      event.preventDefault();
      event.stopPropagation();
      this.#dragging = false;
    }
  };

  /** @param {Event} event */
  #preventDefault = (event) => {
    event.preventDefault();
  };

  /**
   * Scrollt de track met het toetsenbord wanneer de carousel-container zelf
   * focus heeft. We laten interacties met focusbare elementen binnenin (zoals
   * de knoppen) ongemoeid door alleen te reageren als de container het doel is.
   *
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.target !== this || !this.#track) return;

    const { key } = event;
    const handledKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!handledKeys.includes(key)) return;

    event.preventDefault();

    const track = this.#track;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    if (key === 'Home') {
      track.scrollTo({ left: 0, behavior });
      return;
    }

    if (key === 'End') {
      track.scrollTo({ left: track.scrollWidth, behavior });
      return;
    }

    const slide = track.querySelector('.custom-slider__slide');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    const direction = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;

    track.scrollBy({ left: direction * step, behavior });
  };
}

if (!customElements.get('custom-slider-carousel')) {
  customElements.define('custom-slider-carousel', CustomSliderCarousel);
}
